import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, Link } from 'react-router-dom';
import { Row, Col } from 'reactstrap';
import { getOneRosterSchoolUnits } from '../../store/OneRoster/OneRosterSchoolUnitAction';
import StatusCode from '../../util/StatusCode';
import debounce from 'lodash/debounce';
import ActiveToggle from '../misc/ActiveToggle';
import TableHorizontal from '../misc/TableHorizontal';
import * as Helper from '../../util/Helper';
import FilterBottomComponent from '../misc/FilterBottomComponent';
import Search from '../misc/Search';
import RequestParser from '../../util/RequestParser';
import { PageResult } from '../../models/CoreModels';
import { OneRosterSchoolUnit } from '../../models/CoreOneRosterModels';
import { CacheRequestState } from '../../models/RequestState';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';


const OneRosterSchoolUnits = () => {
    const { t } = useTranslation(['common', 'oneRoster']);

    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const schoolUnits = useSelector<RootState, CacheRequestState<PageResult<OneRosterSchoolUnit>>>(state => state.onerosterSchoolunit.schoolUnits);
    var filter = RequestParser.parseSearchPageRequest(searchParams);

    useEffect(() => {
        if (filter.search) {
            setSearch(filter.search);
        }
        getOneRosterSchoolUnits(filter)(dispatch);
    }, [dispatch]);

    useEffect(() => {
        getOneRosterSchoolUnits(filter, true)(dispatch);
        window.scrollTo(0, 0);
    }, [searchParams]);

    const updateFilter = (name: string, value: string | number | boolean) => {
        if (name !== 'page') {
            searchParams.set('page', '1');
        }

        if (value !== undefined && value !== '' && value !== null) {
            if (typeof (value) === 'string') {
                searchParams.set(name, value);
            } else {
                searchParams.set(name, value.toString());
            }
        } else {
            searchParams.delete(name);
        }

        setSearchParams(searchParams);
    }

    const debouncedSearch = useCallback(
        debounce(search => {
            updateFilter('search', search);
        }, 300), [searchParams]);

    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        let search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    }

    const clearFilter = () => {
        setSearch('');
        setSearchParams([]);
    }

    const mapSchoolUnits = () => {
        let onlyActive = filter.onlyActive !== undefined ? filter.onlyActive : true;
        if (schoolUnits.code === StatusCode.COMPLETE || (schoolUnits.code === StatusCode.PENDING && schoolUnits.data !== undefined)) {
            if (schoolUnits.data === undefined) {
                return [];
            }

            return schoolUnits.data?.values.map(s => ({
                id: s.id,
                data: [
                    { value: <Link to={`/oneroster/schoolunit/${s.id}/information`}>{s.name}</Link> },
                    { value: Helper.active(s.active) },
                    { value: <Link to={`/oneroster/class/?search=${s.name}`}> {s.classCount} </Link> },
                    { value: <Link to={`/oneroster/user/?search=${s.name}&role=teacher&onlyActive=${onlyActive}`}>{s.teacherCount}</Link> },
                    { value: <Link to={`/oneroster/user/?search=${s.name}&role=student&onlyActive=${onlyActive}`}>{s.studentCount}</Link> },
                    { value: Helper.formatDate(s.created), className: 'nowrap' },
                    { value: Helper.formatDate(s.modified), className: 'nowrap' },
                ]
            }));
        } else {
            return [];
        }
    }

    const renderSchoolUnits = () => {
        const schoolInfo = mapSchoolUnits();
        return (
            <TableHorizontal
                header={[{ value: t('genericTableHeaders.name', { ns: 'common' }) }, { value: t('genericTableHeaders.active', { ns: 'common' }) }, { value: t('commonTableHeaders.sections', { ns: 'oneRoster' }) }, { value: t('commonTableHeaders.teachers', { ns: 'oneRoster' }) }, { value: t('commonTableHeaders.students', { ns: 'oneRoster' }) }, { value: t('genericTableHeaders.created', { ns: 'common' }) }, { value: t('genericTableHeaders.modified', { ns: 'common' }) }]}
                info={schoolInfo}
                error={schoolUnits.code === StatusCode.ERROR}
                searching={schoolUnits.code === StatusCode.PENDING}
                displayName={t('object.oneRoster', { ns: 'common', count: 2 })}
                noResMsg={t('notFound.multiple', { ns: 'oneRoster', type: t('type.schools', { ns: 'oneRoster' }) })}
            />
        )
    };

    const renderFilter = () => (
        <Row>
            <Col sm="6">
                <Search value={search} onChange={onSearch} placeholder={t('search', { ns: 'oneRoster', type: t('type.school', { ns: 'oneRoster' }) })} />
            </Col>

            <Col sm="6">
                <ActiveToggle
                    onlyActive={filter?.onlyActive ?? true}
                    update={(active) => updateFilter('onlyActive', active)}
                    disabled={schoolUnits.code === StatusCode.PENDING}
                />
            </Col>
        </Row>
    );

    return (
        <div className="container schoolunits_oneroster">
            <div className="header--fixed">
                <h1>{t('object.oneRoster.school', { ns: 'common', count: 2 })}</h1>
                {renderFilter()}
            </div>
            <div className="body--scroll padding-40">
                {renderSchoolUnits()}
            </div>

            <FilterBottomComponent
                clearFilter={clearFilter}
                updateFilter={(value) => updateFilter('page', value)}
                requestState={schoolUnits}
            />

        </div>
    )
}

export default OneRosterSchoolUnits;
